import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Dialog, Field, HStack, NativeSelect, Portal, RadioGroup, Spinner, Text, VStack } from '@chakra-ui/react';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { INVITE_ADVISOR_TO_BOARD } from '@src/gql/mutations/InviteAdvisorToBoard';
import { INVITE_TO_BOARD_OPEN_SEATS } from '@src/gql/queries/InviteToBoardOpenSeats';
import { ScoAdvisorEngagement } from '@src/graphql-types';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
type InviteToBoardModalProps = {
  advisorUserId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};
export const InviteToBoardModal = ({
  advisorUserId,
  isOpen,
  onClose,
  onSuccess
}: InviteToBoardModalProps) => {
  const [inviteMode, setInviteMode] = useState<'seat' | 'direct'>('seat');
  const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const seatsQuery = useQuery(INVITE_TO_BOARD_OPEN_SEATS, {
    skip: !isOpen,
    fetchPolicy: 'network-only'
  });
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const [inviteAdvisorToBoard, inviteMutation] = useMutation(INVITE_ADVISOR_TO_BOARD);
  const openSeats = (seatsQuery.data?.advisorEngagements?.items ?? []).filter((item): item is ScoAdvisorEngagement => !!item?.advisorEngagementId);
  // Stable identity for reset — do not depend on a new array from `.filter()` each render.
  const openSeatsKey = openSeats.map(s => s.advisorEngagementId).join(',');
  const firstOpenSeatId = openSeats[0]?.advisorEngagementId ?? null;
  const hasOpenSeats = openSeats.length > 0;
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setInviteMode(hasOpenSeats ? 'seat' : 'direct');
    setSelectedSeatId(firstOpenSeatId);
    setMessage('');
    setErrorMessage(null);
    // Reset only when the modal opens or the open-seat set identity changes, not every render.
  }, [isOpen, openSeatsKey, hasOpenSeats, firstOpenSeatId]);
  const handleSubmit = async () => {
    setErrorMessage(null);
    try {
      // Prefer returned entity fields for cache identity; re-read open seats so the
      // filled seat drops out of draft/searching without a full page reload.
      await inviteAdvisorToBoard({
        variables: {
          input: {
            advisorUserId,
            advisorEngagementId: inviteMode === 'seat' ? selectedSeatId : undefined,
            message: message.trim() || undefined
          }
        },
        // Refresh invite modal seats + board seats list (ADV-B3 invite cache honesty)
        refetchQueries: [{
          query: INVITE_TO_BOARD_OPEN_SEATS
        }, 'BoardSeatsSectionEngagements', 'DirectoryFounderOpenSeatMatches'],
        awaitRefetchQueries: true
      });
      onSuccess?.();
      onClose();
      const invitedSeatId = inviteMode === 'seat' ? selectedSeatId : undefined;
      void router.push(invitedSeatId ? `${paths.seats}/${invitedSeatId}` : paths.myAdvisorsInvited);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Could not send the invite.');
    }
  };
  return <Dialog.Root open={isOpen} placement={'center'} onOpenChange={event => {
    if (!event.open) {
      onClose();
    }
  }}>
      {/* Portal out of panel overflow so the modal isn't covered by the toolbar. */}
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bgColor={'colorPalette.container'} maxWidth={'lg'} padding={6} width={'full'}>
          <Dialog.Header>
            <Dialog.Title>Invite to advisory board</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <VStack align={'stretch'} gap={4}>
              <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                Choose an open seat or send a direct invite. They&apos;ll get a notification to accept or
                decline.
              </Text>

              {openSeats.length === 0 && !seatsQuery.loading && <Button asChild size={'sm'} variant={'outline'} width={'fit-content'}>
                  <NextLink href={paths.seatNew}>Create a board seat</NextLink>
                </Button>}

              <RadioGroup.Root value={inviteMode} onValueChange={event_0 => setInviteMode(event_0.value as 'seat' | 'direct')}>
                <HStack align={'stretch'} gap={3} wrap={'wrap'}>
                  <RadioGroup.Item disabled={openSeats.length === 0} value={'seat'}>
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemControl />
                    <RadioGroup.ItemText>Into an open seat</RadioGroup.ItemText>
                  </RadioGroup.Item>
                  <RadioGroup.Item value={'direct'}>
                    <RadioGroup.ItemHiddenInput />
                    <RadioGroup.ItemControl />
                    <RadioGroup.ItemText>Direct invite</RadioGroup.ItemText>
                  </RadioGroup.Item>
                </HStack>
              </RadioGroup.Root>

              {inviteMode === 'seat' && <Field.Root>
                  <Field.Label>Open seat</Field.Label>
                  {seatsQuery.loading ? <Spinner size={'sm'} /> : openSeats.length === 0 ? <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
                      No open seats right now. Use a direct invite or{' '}
                      <Box as={'span'} color={'colorPalette.fg'} textDecoration={'underline'}>
                        <NextLink href={paths.seatNew}>create a seat</NextLink>
                      </Box>{' '}
                      first.
                    </Text> : <NativeSelect.Root>
                      <NativeSelect.Field value={selectedSeatId ?? ''} onChange={event_1 => setSelectedSeatId(event_1.currentTarget.value || null)}>
                        {openSeats.map(seat => <option key={seat.advisorEngagementId} value={seat.advisorEngagementId!}>
                            {seat.category?.title || seat.categoryKey}
                          </option>)}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>}
                </Field.Root>}

              <Field.Root>
                <Field.Label>Message (optional)</Field.Label>
                <AutoResizeTextarea bg={'colorPalette.subtle'} fontFamily={'longform'} placeholder={'Tell them why you want them on your board…'} value={message} onChange={event_2 => setMessage(event_2.currentTarget.value)} />
              </Field.Root>

              {errorMessage && <Text color={'red.fg'} fontSize={'sm'}>
                  {errorMessage}
                </Text>}
            </VStack>
          </Dialog.Body>
          <Dialog.Footer>
            <HStack gap={2} justify={'flex-end'} width={'full'}>
              <Button variant={'outline'} onClick={onClose}>
                Cancel
              </Button>
              <Button disabled={inviteMode === 'seat' && (!selectedSeatId || openSeats.length === 0)} loading={inviteMutation.loading} variant={'cta'} onClick={handleSubmit}>
                Send invite
              </Button>
            </HStack>
          </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>;
};